// El panel falla solo; la pagina de detras nunca (D6, R6, CA5).
//
// **Por que hace falta una pieza y no basta con el `catch` del envio.** Ese
// `catch` cubre lo que ya se sabia que podia fallar: que el Brain no conteste
// (`use-brain-conversation.ts:91`). Lo que no cubre es que el panel reviente al
// pintarse —un mensaje con una forma que no esperaba, un rastro a medias, una
// herramienta que devuelve algo raro—, y eso en React no es un fallo local:
// **desmonta el arbol entero**. Sin esto, el panel se lleva por delante la
// pagina que estabas usando, que es justo lo que R6 prohibe.
//
// Es el primer limite de fallo del repositorio: al escribirlo no habia ni un
// `componentDidCatch` en `frontend-react`. Se pone **solo alrededor del
// panel**, a proposito: un limite en la raiz convierte cualquier fallo en una
// pantalla en blanco con un mensaje amable, que es peor que el error.

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { log } from '@/lib/logger'

/** Lo que se ve en lugar del panel. Aparte porque un componente de clase no
 *  puede usar hooks, y el texto tiene que estar traducido igual que el resto. */
export function PanelRoto({ onCerrar }: { onCerrar: () => void }) {
  const { t } = useTranslation()
  return (
    <aside
      aria-label={t('brain.title')}
      className="flex h-full min-h-0 flex-col items-start gap-4 bg-background p-6"
    >
      <p className="text-sm text-muted-foreground">{t('brain.panel.roto')}</p>
      {/* Un panel roto que no se puede cerrar es peor que el fallo. */}
      <Button size="sm" variant="outline" onClick={onCerrar}>
        {t('brain.panel.cerrar')}
      </Button>
    </aside>
  )
}

interface Props {
  children: ReactNode
  onCerrar: () => void
}

export class PanelErrorBoundary extends Component<Props, { rompio: boolean }> {
  state = { rompio: false }

  static getDerivedStateFromError() {
    return { rompio: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    log.error('El panel del Brain se rompio al pintarse', error, {
      component: 'PanelErrorBoundary',
      componentStack: info.componentStack,
    })
  }

  render() {
    if (this.state.rompio) return <PanelRoto onCerrar={this.props.onCerrar} />
    return this.props.children
  }
}
